// For now this will FE feature flags
// but the file is in the style of LaunchDarkly feature flags

import { getStoredUser } from '@/contexts/auth-context';
import {
  JIMMY_EMAIL,
  LASSE_EMAIL,
  MAX_EMAIL,
  TJ_EMAIL,
  useIsBDC,
  useIsBusinessOffice,
  useIsBuyer,
  useIsContractor,
  useIsController,
  useIsEngineer,
  useIsFinanceManager,
  useIsFounder,
  useIsGeneralManager,
  useIsManager,
  useIsOps,
  useIsSalesManager,
  useIsLogisticsManager,
} from '@/lib/get-user-role';
import { useMemo } from 'react';

export const FEATURE_FLAG_NAMES = {
  POLYMORPHIC_COMMENTS_REPLIES: 'polymorphic-comments-replies',
  AGENT_CONVO: 'agent-convo',
  AGENT_CONVO_REPLY: 'agent-convo-reply',
  AI_PRIORITIZE_TASKS: 'ai-prioritize-tasks',
  AI_SUGGESTIONS: 'ai-suggestions',
  ALL_TASKS: 'all-tasks',
  DEID_THREAD: 'deid-thread',
  MY_TASKS_COPILOT: 'my-tasks-copilot',
  TEXT_CONVO: 'text-convo',
  TEXT_RECAP: 'text-recap',
  LEADS_REGENERATE: 'leads-regenerate',
  FULL_LEADS_REFRESH: 'full-leads-refresh',
  INSTANT_OFFERS: 'instant-offers',
  REACTIVATE_DEAL: 'reactivate-deal',
  PLAID_WORKFLOW: 'plaid-workflow',
  DEAL_DRAFT_PROMOTION: 'deal-draft-promotion',
  ARCHIVE_DEAL: 'archive-deal',
  GENERALIZED_TASKS: 'generalized-tasks',
  FPS_METER: 'fps-meter',
  RETURN_VEHICLE: 'return-vehicle',
  OVERRIDE_DEAL_STATUS: 'override-deal-status',
};

export type FeatureFlagName =
  (typeof FEATURE_FLAG_NAMES)[keyof typeof FEATURE_FLAG_NAMES];

export const useIsFeatureEnabled = ({
  featureFlagName,
}: {
  featureFlagName: FeatureFlagName;
}) => {
  const { isFounder } = useIsFounder();
  const { isContractor } = useIsContractor();
  const { isEngineer, isJimmy, isTJ, isArjun, isRandy } = useIsEngineer();
  const { isFinanceManager } = useIsFinanceManager();
  const { isGeneralManager } = useIsGeneralManager();
  const { isManager } = useIsManager();
  const { isSalesManager } = useIsSalesManager();
  const { isOps } = useIsOps();
  const { isBusinessOffice } = useIsBusinessOffice();
  const { isController } = useIsController();
  const { isBDC } = useIsBDC();
  const { isBuyer } = useIsBuyer();
  const { isLogisticsManager } = useIsLogisticsManager();
  const isFeatureEnabled = useMemo(() => {
    switch (featureFlagName) {
      case FEATURE_FLAG_NAMES.AGENT_CONVO:
        return true;
      case FEATURE_FLAG_NAMES.AGENT_CONVO_REPLY:
        return isFounder || isJimmy || isArjun;
      case FEATURE_FLAG_NAMES.AI_PRIORITIZE_TASKS:
        return isFounder || isJimmy || isGeneralManager || isSalesManager;
      case FEATURE_FLAG_NAMES.AI_SUGGESTIONS:
        return (
          isFounder || isJimmy || isArjun || isGeneralManager || isSalesManager
        );
      case FEATURE_FLAG_NAMES.ALL_TASKS:
        return isFounder || isEngineer || isFinanceManager || isGeneralManager;
      case FEATURE_FLAG_NAMES.MY_TASKS_COPILOT:
        return (
          isFounder || isJimmy || isArjun || isGeneralManager || isSalesManager
        );
      case FEATURE_FLAG_NAMES.TEXT_CONVO:
        return isFounder || isJimmy || isArjun;
      case FEATURE_FLAG_NAMES.TEXT_RECAP:
        return isEngineer || isFounder || isFinanceManager || isGeneralManager;
      case FEATURE_FLAG_NAMES.DEID_THREAD:
        return false;
      case FEATURE_FLAG_NAMES.LEADS_REGENERATE:
        return isFounder || isJimmy || isGeneralManager || isSalesManager;
      case FEATURE_FLAG_NAMES.FULL_LEADS_REFRESH:
        return isTJ;
      case FEATURE_FLAG_NAMES.INSTANT_OFFERS:
        return isFounder || isEngineer;
      case FEATURE_FLAG_NAMES.REACTIVATE_DEAL:
        return isManager || isFounder || isJimmy;
      case FEATURE_FLAG_NAMES.PLAID_WORKFLOW:
        return isFounder || isFinanceManager || isGeneralManager;
      case FEATURE_FLAG_NAMES.DEAL_DRAFT_PROMOTION:
        return isFounder || isManager || isBDC;
      case FEATURE_FLAG_NAMES.ARCHIVE_DEAL:
        return (
          isEngineer ||
          isFinanceManager ||
          isGeneralManager ||
          isFounder ||
          isContractor ||
          isOps ||
          isBusinessOffice ||
          isController ||
          isBuyer ||
          isLogisticsManager
        );
      case FEATURE_FLAG_NAMES.POLYMORPHIC_COMMENTS_REPLIES:
        return (
          isFounder || isJimmy || isArjun || isGeneralManager || isSalesManager
        );
      case FEATURE_FLAG_NAMES.GENERALIZED_TASKS:
        return true;
      case FEATURE_FLAG_NAMES.FPS_METER:
        return isTJ || isJimmy || isRandy;
      case FEATURE_FLAG_NAMES.RETURN_VEHICLE:
        return isFinanceManager || isGeneralManager || isEngineer || isFounder;
      case FEATURE_FLAG_NAMES.OVERRIDE_DEAL_STATUS:
        return isJimmy;
      default:
        return false;
    }
  }, [
    featureFlagName,
    isFounder,
    isJimmy,
    isEngineer,
    isFinanceManager,
    isGeneralManager,
    isSalesManager,
    isArjun,
  ]);

  return { isFeatureEnabled };
};

const isInvestorDemoCrew = (): boolean => {
  const user = getStoredUser();

  return [JIMMY_EMAIL, TJ_EMAIL, LASSE_EMAIL, MAX_EMAIL].includes(user?.email);
};

export const optimizeDealsForInvestorDemo = (): boolean => {
  return isInvestorDemoCrew();
};
